import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { InstagramLogo, ArrowUpRight, Play } from "@phosphor-icons/react";
import api from "@/lib/api";

const PROFILE_URL = "https://www.instagram.com/lli.hyd";

const FollowCard = () => (
  <motion.a
    href={PROFILE_URL}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="group flex items-center justify-between gap-6 border border-forest/20 bg-forest text-bone p-8 lg:p-10 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
    data-testid="instagram-follow-card"
  >
    <div className="flex items-center gap-5">
      <span className="w-14 h-14 shrink-0 border border-bone/30 flex items-center justify-center group-hover:bg-terra group-hover:border-terra transition-colors duration-300">
        <InstagramLogo size={26} weight="fill" />
      </span>
      <div>
        <p className="font-serif font-bold text-2xl tracking-tight">@lli.hyd</p>
        <p className="text-sm text-bone/60 mt-1">Fresh classroom moments, every week — follow along.</p>
      </div>
    </div>
    <ArrowUpRight size={28} className="shrink-0 text-bone/50 group-hover:text-terra group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
  </motion.a>
);

export const InstagramFeed = () => {
  const [state, setState] = useState({ status: "loading", posts: [] });

  useEffect(() => {
    api.get("/instagram/feed", { params: { limit: 8 } })
      .then((r) => setState({ status: r.data.posts?.length ? "ready" : "unavailable", posts: r.data.posts || [] }))
      .catch(() => setState({ status: "unavailable", posts: [] }));
  }, []);

  return (
    <section className="py-24 lg:py-32 border-t border-forest/15" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-12"
        >
          <div>
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra">On Instagram</span>
            <h2 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl lg:text-6xl mt-4" data-testid="instagram-heading">
              Fresh from the <span className="italic text-terra">classroom.</span>
            </h2>
          </div>
          <a href={PROFILE_URL} target="_blank" rel="noopener noreferrer" data-testid="instagram-profile-link"
            className="text-xs font-bold tracking-[0.2em] uppercase text-moss hover:text-terra transition-colors flex items-center gap-2">
            Follow @lli.hyd <ArrowUpRight size={14} />
          </a>
        </motion.div>

        {state.status === "loading" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="instagram-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-square bg-bonealt animate-pulse border border-forest/10" />
            ))}
          </div>
        )}

        {state.status === "unavailable" && <FollowCard />}

        {state.status === "ready" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="instagram-grid">
            {state.posts.map((post, i) => {
              const image = post.media_type === "VIDEO" ? post.thumbnail_url : post.media_url;
              return (
                <motion.a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                  className="group relative aspect-square overflow-hidden border border-forest/15 bg-bonealt"
                  data-testid={`instagram-post-${i}`}
                >
                  <img
                    src={image}
                    alt={(post.caption || "Learning Lights Initiative on Instagram").slice(0, 120)}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {post.media_type === "VIDEO" && (
                    <span className="absolute top-3 right-3 w-8 h-8 bg-forest/80 text-bone flex items-center justify-center">
                      <Play size={14} weight="fill" />
                    </span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest/90 to-transparent p-4 pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {post.caption && <span className="block text-xs text-bone/90 leading-snug line-clamp-2">{post.caption}</span>}
                  </span>
                </motion.a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
